import { useAuth } from '@/hooks/useAuth';

/**
 * Prefisso i18n del lessico del denaro, calcolato dal ruolo di chi sta guardando.
 *
 * Regola unica, valida ovunque: i soldi si raccontano SEMPRE dal lato di chi paga, cioe'
 * il cliente. Nessuna schermata dice piu' "incassato" -- non perche' sia sbagliato, ma
 * perche' costringeva a mantenere due letture degli stessi numeri e la stessa cifra
 * cambiava nome a seconda della pagina. Quello che cambia col ruolo e' solo la persona:
 *
 *   traveler                      -> `money.self`   -- e' lui che paga, gli si parla in
 *                                    seconda persona ("Non hai ancora pagato...")
 *   admin, agency, TD, TA member  -> `money.client` -- guardano il conto di qualcun
 *                                    altro, quindi terza persona ("Nessun pagamento
 *                                    registrato finora.")
 *
 * Il prefisso e' tipizzato come unione delle due stringhe letterali: interpolarlo con una
 * foglia (`${money}.paid`) produce una `TranslationKey` valida senza cast, e una foglia
 * che manca in `money` non compila. In JSX si usa cosi':
 *
 *   const money = useMoneyCopy();
 *   <span>{t(`${money}.paid`)}</span>
 *
 * Ogni foglia va tenuta in ENTRAMBI i blocchi, in ENTRAMBE le lingue: e' il prezzo dello
 * sdoppiamento, ed e' anche cio' che permette di far divergere una frase senza rincorrere
 * i componenti che la usano.
 */
export type MoneyCopyKey = 'money.self' | 'money.client';

export function useMoneyCopy(): MoneyCopyKey {
    const { userRole } = useAuth();
    // Il default non e' `self`: un ruolo non ancora caricato e' quasi sempre un ruolo
    // operativo che sta montando la pagina, e dare del "tu" a un'agenzia si nota molto
    // piu' del contrario.
    return userRole === 'traveler' ? 'money.self' : 'money.client';
}
